import { 
	Autocomplete,
	Box,
	Button, 
	Dialog, 
	DialogActions, 
	DialogContent, 
	DialogContentText, 
	DialogTitle, 
	TextField 
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useReservedForm } from "store/ReservedFormProvider";
import { useData } from "store/DataProvider";
import { useNetworkStatus } from "hooks/useNetworkStatus";
import { useVolunteerController } from "hooks/useVolunteerController";
import { initIndexedDbIfMissingStore } from "utils/indexedDbRecovery";
import { getVolunteerIdsWithActiveFlagbagOffline } from "utils/volunteerSpareAssignment";

interface VolunteerOption {
	volunteerId: string;
	name: string;
	mobileNum: string;
	email: string;
}

interface ReservedFormProps {
	handleSubmit: (data: { volunteerId: string }) => void;
	open?: boolean;
	onClose?: () => void;
	assigningFlagBagId?: string;
}

const ReservedForm = ({
	handleSubmit,
	open: openProp,
	onClose: onCloseProp,
	assigningFlagBagId: assigningFlagBagIdProp,
}: ReservedFormProps) => {
	const reservedFormContext = useReservedForm();
	const open = openProp ?? reservedFormContext?.reservedFormOpen ?? false;
	const close = onCloseProp ?? reservedFormContext?.close ?? (() => {});
	const assigningFlagBagId = assigningFlagBagIdProp ?? reservedFormContext?.assigningFlagBagId;
	const [volunteers, setVolunteers] = React.useState<VolunteerOption[]>([]);
	const [selectedVolunteer, setSelectedVolunteer] = React.useState<VolunteerOption | null>(null);
	const [loading, setLoading] = React.useState(false);
	const data = useData();
	const { isOnline } = useNetworkStatus();
	const volunteerController = useVolunteerController();
	const offlineDB = initIndexedDbIfMissingStore("offline");
	const flagBagDB = initIndexedDbIfMissingStore("flagbag");
	const [error, setError] = React.useState(false);
	const eventId = data.getEventId();
	const stationId = data.getStationId();

	// Filter volunteers by station; active uncollected flagbags filtered via cache search below
	const filterVolunteersByStation = (volunteers: any[]): VolunteerOption[] => {
		if (!stationId) {
			return [];
		}
		
		return volunteers
			.filter((vol) => {
				// 1. 筛选出属于当前站点的志愿者：
				//    - flagbagStationId 等于当前站点ID
				//    - 或者 managedStationIds 数组中包含当前站点ID
				const belongsToStation = vol.flagbagStationId === stationId || 
				                         (vol.managedStationIds && Array.isArray(vol.managedStationIds) && vol.managedStationIds.includes(stationId));
				
				return belongsToStation;
			})
			.map((vol) => ({
				volunteerId: vol.volunteerId,
				name: vol.name,
				mobileNum: vol.mobileNum,
				email: vol.email || "",
			}));
	};

	const filterVolunteersWithUncollectedFlagbag = React.useCallback(
		async (volunteerList: VolunteerOption[]): Promise<VolunteerOption[]> => {
			const blockedVolunteerIds = await getVolunteerIdsWithActiveFlagbagOffline(
				offlineDB,
				flagBagDB,
				assigningFlagBagId ?? undefined,
				stationId,
			);
			return volunteerList.filter((vol) => !blockedVolunteerIds.has(vol.volunteerId));
		},
		[offlineDB, flagBagDB, assigningFlagBagId, stationId],
	);

	React.useEffect(() => {
		if (!open || !eventId || !stationId) return;
		
		let isMounted = true;
		
		const loadVolunteers = async () => {
			try {
				if (isMounted) setLoading(true);
				
				const cachedVolunteers = await volunteerController.getVolunteersFromCache(eventId);
				
				if (isOnline) {
					const freshVolunteers = await volunteerController.fetchAndSaveVolunteers(eventId);
					if (isMounted) {
						const volunteerList = await filterVolunteersWithUncollectedFlagbag(
							filterVolunteersByStation(freshVolunteers),
						);
						setVolunteers(volunteerList);
						setLoading(false);
					}
				} else if (cachedVolunteers.length > 0) {
					const cachedList = await filterVolunteersWithUncollectedFlagbag(
						filterVolunteersByStation(cachedVolunteers),
					);
					if (isMounted) {
						setVolunteers(cachedList);
						setLoading(false);
					}

				} else {
					if (isMounted) setLoading(false);
				}
			} catch (error) {
				console.error("Failed to load volunteers:", error);
				if (isMounted) {
					enqueueSnackbar("無法獲取義工列表", { variant: "error" });
					setLoading(false);
				}
			}
		};
		
		loadVolunteers();
		
		return () => {
			isMounted = false;
		};
	}, [open, eventId, stationId, isOnline, filterVolunteersWithUncollectedFlagbag]);

	React.useEffect(() => {
		if (open) {
			setSelectedVolunteer(null);
			setError(false);
		}
	}, [open]);

	const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		
		if (!selectedVolunteer) {
			setError(true);
			enqueueSnackbar("請選擇義工", { variant: "error" });
			return;
		}
		
		handleSubmit({ volunteerId: selectedVolunteer.volunteerId });
		close();
	};

	return (
		<Dialog
			open={open}
			onClose={close}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				component: "form",
				onSubmit: handleFormSubmit,
				sx: {
					borderRadius: 2,
				}
			}}
			sx={{ 
				'& .MuiDialog-container': {
					'& .MuiPaper-root': {
						margin: 2,
					}
				}
			}}
		>
			<DialogTitle sx={{ pb: 1 }}>
				後備旗袋
			</DialogTitle>
			<DialogContent sx={{ pt: 2 }}>
				<DialogContentText sx={{ mb: 2 }}>
					請選擇義工
				</DialogContentText>
				<Autocomplete<VolunteerOption>
					value={selectedVolunteer}
					onChange={(event, newValue) => {
						setSelectedVolunteer(newValue);
						setError(false);
					}}
					options={volunteers}
					loading={loading}
					disabled={volunteers.length === 0 && !isOnline}
					getOptionLabel={(option) => option.name}
					isOptionEqualToValue={(option, value) => option.volunteerId === value.volunteerId}
					renderOption={(props, option) => {
						const { key, ...otherProps } = props;
						return (
							<Box 
								component="li" 
								key={key}
								{...otherProps} 
								sx={{ 
									display: 'flex', 
									flexDirection: 'column', 
									alignItems: 'flex-start',
									justifyContent: 'flex-start',
									textAlign: 'left',
									py: 1.5
								}}
							>
								<Box sx={{ fontWeight: 500, fontSize: '1rem', textAlign: 'left', width: '100%' }}>{option.name}</Box>
								{option.email && (
									<Box sx={{ fontSize: '0.875rem', color: 'text.secondary', textAlign: 'left', width: '100%' }}>
										Email: {option.email}
									</Box>
								)}
							</Box>
						);
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							autoFocus
							label="義工*"
							error={error}
							placeholder="搜尋義工姓名"
							helperText={
								loading
									? "載入中..."
									: !isOnline && volunteers.length === 0
									? "離線模式且無緩存數據" 
									: !isOnline && volunteers.length > 0
									? `離線模式（使用緩存的 ${volunteers.length} 位義工）`
									: error 
									? "請選擇義工" 
									: volunteers.length > 0
									? `共 ${volunteers.length} 位義工`
									: ""
							}
							InputProps={{
								...params.InputProps,
								sx: { borderRadius: 1 }
							}}
						/>
					)}
					noOptionsText={loading ? "加載中..." : "沒有可用的義工"}
					loadingText="載入中..."
					disablePortal={false}
					componentsProps={{
						popper: {
							sx: {
								zIndex: 1600,
							}
						}
					}}
					sx={{
						'& .MuiAutocomplete-listbox': {
							maxHeight: '300px',
							'& .MuiAutocomplete-option': {
								textAlign: 'left',
								justifyContent: 'flex-start',
							}
						}
					}}
				/>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={close} color="inherit">
					取消
				</Button>
				<Button 
					type="submit" 
					variant="contained"
					disabled={!selectedVolunteer}
				>
					確定
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ReservedForm;
